import React, { useMemo } from 'react'
import { Drawer, Empty, Flex, List, Result, Tag } from 'antd'
import { questionActions } from '../../../../../../store/slices/question.slice'
import { useDispatch, useSelector } from 'react-redux'
import {
    useFetchQuestionChangesHistoryQuery,
    useFetchQuestionsQuery,
} from '../../../../../../store/apis/question.api'
import { useFetchActiveBoostersQuery } from '../../../../../../store/apis/booster.api'
import questionSelectors from '../../../../../../store/selectors/question.selectors'
import Text from 'antd/es/typography/Text'
import { sqlToJsDate, sqlToTimeFromNow } from '../../../../../../utils/date.helper'
import { fetchQuestionsSelectors } from '../../../../../../store/apis/utils/selectors/question.api.selectors'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import { useParams } from 'react-router'
import { isNumber } from 'lodash'
import Avatar from '../../../../../../components/common/avatar/Avatar'
import { getActorIcon, getActorName } from '../../../../../../constants/actor-types'
import { getColorByIndex, COLORS } from '../../../../../../utils/colors.helper'
import { useThemeColors } from '../../../../../../hooks/useThemeColors'
import AiIcon from '../../../../../../components/common/icons/AiIcon'
import { getChangeType } from './utils/changeValueParser'
import ChangeTarget from './components/ChangeTarget'
import {
    BoosterChangeDisplay,
    ConceptualLabelChangeDisplay,
    PatternCreatedDisplay,
    ColorChangeDisplay,
    BooleanChangeDisplay,
    DefaultChangeDisplay,
} from './components/ChangeValueDisplay'

const ChangesHistoryDrawer = () => {
    const colors = useThemeColors()
    const editingQuestionId = useSelector(questionSelectors.editingQuestionId)
    const dispatch = useDispatch()
    const versionId = useVersionId()
    const { token } = useParams()

    const {
        data: history,
        isFetching,
        error,
    } = useFetchQuestionChangesHistoryQuery({
        id: editingQuestionId,
        versionId,
    })
    const { data: editingQuestion } = useFetchQuestionsQuery(
        { token, versionId },
        {
            selectFromResult: result => fetchQuestionsSelectors.byId({ id: editingQuestionId }, result),
        },
    )

    // Fetch active boosters to get display names
    const { data: activeBoosters = [] } = useFetchActiveBoostersQuery()

    const handleClose = () => dispatch(questionActions.setShowChangesHistoryDrawer(false))

    // Prepare lookup data for rendering
    const colorsWithIds = useMemo(() => {
        return COLORS.map((color, index) => ({ ...color, id: index }))
    }, [])

    if (error) return <Result status='error' title='Changes History Fetch Failed' subTitle={error} />

    const renderChangeValue = (item, changeType) => {
        const oldValue = item.old_value ?? item.oldValue
        const newValue = item.new_value ?? item.newValue

        switch (changeType) {
            case 'booster_added':
            case 'booster_removed':
            case 'booster_priority':
                return (
                    <BoosterChangeDisplay
                        oldValue={oldValue}
                        newValue={newValue}
                        changeType={changeType}
                        activeBoosters={activeBoosters}
                    />
                )

            case 'conceptual_label_added':
            case 'conceptual_label_removed':
                return (
                    <ConceptualLabelChangeDisplay
                        oldValue={oldValue}
                        newValue={newValue}
                        changeType={changeType}
                    />
                )

            case 'pattern_created':
                return <PatternCreatedDisplay newValue={newValue} />

            case 'color':
            case 'category_color':
                return <ColorChangeDisplay oldValue={oldValue} newValue={newValue} colors={colorsWithIds} />
            case 'boolean':
                return <BooleanChangeDisplay oldValue={oldValue} newValue={newValue} />

            default:
                return <DefaultChangeDisplay oldValue={oldValue} newValue={newValue} />
        }
    }

    return (
        <Drawer title='Changes History' autoFocus onClose={handleClose} open width={560} destroyOnClose>
            <List
                dataSource={history}
                loading={{ spinning: isFetching, tip: 'Loading history...' }}
                locale={{ emptyText: <Empty description='Changes History Is Empty' /> }}
                renderItem={item => {
                    // Handle both old (camelCase) and new (snake_case) field names
                    const field = item.updated_field ?? item.field
                    const createdAt = item.created_at ?? item.createdAt
                    const actorData = item.actor
                    const isRubricRefinement = item.is_rubric_refinement ?? item.isRubricRefinement

                    const changeType = getChangeType(field)

                    const changeDate = (
                        <Text type='secondary' title={sqlToJsDate(createdAt)}>
                            {sqlToTimeFromNow(createdAt)}
                        </Text>
                    )

                    // Get actor details
                    const actorName = getActorName(actorData)
                    const actorIcon = getActorIcon(actorData)
                    const actorColor = isNumber(actorData?.id)
                        ? getColorByIndex(actorData?.id).value
                        : colors.primary

                    return (
                        <List.Item key={createdAt}>
                            <Flex vertical gap={8}>
                                <Flex gap={4} align='center' wrap='wrap'>
                                    <Avatar
                                        icon={actorIcon}
                                        size='small'
                                        name={actorName}
                                        color={actorColor}
                                        className='mr-8'
                                    />
                                    <Text strong>{actorName}</Text>
                                    <ChangeTarget field={field} editingQuestion={editingQuestion} />
                                    {changeDate}
                                    {isRubricRefinement && (
                                        <Tag icon={<AiIcon />} color='purple' bordered={false}>
                                            Rubric Refinement
                                        </Tag>
                                    )}
                                </Flex>
                                {renderChangeValue(item, changeType)}
                            </Flex>
                        </List.Item>
                    )
                }}
            />
        </Drawer>
    )
}

export default ChangesHistoryDrawer
